import type { Fragment } from "../types.js";
import { brandLogo } from "./brandLogo.js";
import { colors } from "../../brand/theme.js";

export type WatermarkPosition = "tl" | "tr" | "bl" | "br";

/**
 * Brand watermark — pure logo, pinned absolute, frei positionierbar.
 *
 * Look & feel: NACKTES Logo wie ein normales Bild. Kein Pill, kein Backplate,
 * kein Shadow, kein Border, kein Blur — der User will das Logo "wie ein Bild"
 * frei verschieben und live skalieren können. Die Größe entspricht der
 * visuellen Logo-Größe (Höhe = `size`px), nicht der Pill-Höhe.
 */
export function watermark(opts: {
  id: string;
  width: number;
  height: number;
  enabled?: boolean;
  position?: WatermarkPosition;
  opacity?: number;
  size?: number;
  paddingPx?: number;
  offsetX?: number;
  offsetY?: number;
  /** Absolute Position (Mittelpunkt in % der Canvas). Wenn beide gesetzt sind,
   *  überschreiben sie die corner-anchor-Logik komplett — frei platzierbar. */
  xPct?: number;
  yPct?: number;
}): Fragment {
  const {
    id,
    width,
    height,
    enabled = true,
    position = "br",
    opacity = 0.78,
    size = 60,
    paddingPx = 28,
    offsetX = 0,
    offsetY = 0,
    xPct,
    yPct,
  } = opts;

  if (!enabled) return { html: "" };

  const innerLogo = brandLogo({ id: `${id}-logo`, size, color: colors.fg, showName: false });

  // Pures Logo: keine Pill, kein Shadow, kein Border. Nur Opacity + Position.
  const baseStyle = `position:absolute;opacity:${opacity};pointer-events:none;display:inline-flex;align-items:center;justify-content:center;line-height:0;z-index:30;`;

  // Frei positioniert (Drag-on-Canvas): Mittelpunkt bei xPct/yPct, Translate
  // zentriert das Element exakt auf die Cursor-Position. Dadurch liegt das
  // Logo dort, wo der User es loslässt — keine Eckankerung, kein Snap.
  let placement: string;
  if (typeof xPct === "number" && typeof yPct === "number") {
    placement = `left:${xPct}%;top:${yPct}%;transform:translate(-50%, -50%);`;
  } else {
    const offsetXPx = (offsetX / 100) * width;
    const offsetYPx = (offsetY / 100) * height;
    const horizSign = position.includes("l") ? 1 : -1;
    const vertSign = position.includes("t") ? 1 : -1;
    const positionAttr = position.includes("t") ? "top" : "bottom";
    const horizAttr = position.includes("l") ? "left" : "right";
    placement = `${positionAttr}:${paddingPx + vertSign * offsetYPx}px;${horizAttr}:${paddingPx + horizSign * offsetXPx}px;`;
  }

  const html = `<div id="${id}" class="watermark" data-wm="1" style="${baseStyle}${placement}">
${innerLogo.html}
</div>`;

  return { html };
}
